/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { LuisSidebar } from 'src/core/components/LuisSidebar';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useIcons } from 'src/core/hooks/useIcons';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LuisSidebarItemProps } from 'src/core/types/luisSidebar';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

type CluSidebarProps = { serviceType: CluProjectType };

const messages = defineMessages({
    tag: { id: 'clu.cluSidebar.tag', defaultMessage: 'Data labeling' },
    train: { id: 'clu.cluSidebar.train', defaultMessage: 'Training jobs' },
    build: { id: 'clu.cluSidebar.build', defaultMessage: 'Schema definition' },
    home: { id: 'clu.cluSidebar.home', defaultMessage: 'Language Studio' },
    deploy: { id: 'clu.cluSidebar.deploy', defaultMessage: 'Deploying a model' },
    projects: { id: 'clu.cluSidebar.projects', defaultMessage: 'Projects' },
    improve: { id: 'clu.cluSidebar.improve', defaultMessage: 'Improve model' },
    testModel: { id: 'clu.cluSidebar.testModel', defaultMessage: 'Testing deployments' },
    modelDetails: { id: 'clu.cluSidebar.modelDetails', defaultMessage: 'Model performance' },
    preview: { id: 'clu.cluSidebar.preview', defaultMessage: '(Preview)' },
    projectSettings: { id: 'clu.cluSidebar.projectSettings', defaultMessage: 'Project settings' },
    reviewPredictions: { id: 'clu.cluSidebar.reviewPredictions', defaultMessage: 'Review predictions' },
    reviewEndpoint: { id: 'clu.cluSidebar.reviewEndpoint', defaultMessage: 'Review endpoint utterances' }
});

export const CluSidebar = observer((props: CluSidebarProps) => {
    const { serviceType } = props;
    const history = useHistory();
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const { rootStore, uiStore } = useGlobalState();
    useIcons(['MachineLearning']);

    const { currentCluProjectStore } = rootStore;
    const currentProjectName = currentCluProjectStore?.name;
    const routes = routeTable[serviceType === 'Conversation' ? 'clu' : 'orchestration'];

    const moreDetailsItemIdRef = React.useRef<string>(getId());

    React.useEffect(() => () => dequeueTeachingBubble('CluExamineModel'));

    React.useEffect(() => {
        if (
            uiStore.teachingBubbles.CluTrainModelPage &&
            !uiStore.teachingBubbles.CluExamineModel &&
            !uiStore.skippedTeachingBubbles.CluTrainModelPage &&
            currentProjectName
        ) {
            createTeachingBubble(
                'CluExamineModel',
                localize(cluTeachingBubbleMessages.examineModelBody),
                localize(cluTeachingBubbleMessages.examineModelTitle),
                `#${moreDetailsItemIdRef.current}`,
                true
            );
        }
    }, [Object.values(uiStore.teachingBubbles), currentProjectName]);

    const items: LuisSidebarItemProps[] = [
        {
            iconName: 'Home',
            text: localize(messages.home),
            onClick: () => history.push(routeTable.languageStudio.root()),
            isSelected: doesRouteMatchLocation(routeTable.languageStudio.root(), { exact: true, locationToMatch: pathname })
        },
        {
            forceNoWrap: true,
            iconName: 'AlignLeft',
            text: localize(messages.projects),
            onClick: () => history.push(routes.projectsPage()),
            isSelected: doesRouteMatchLocation(routes.projectsPage(), { exact: true, locationToMatch: pathname })
        },
        { itemType: 'Header', text: `${currentProjectName}` },
        {
            iconName: 'Manufacturing',
            text: localize(messages.build),
            isSelected: doesRouteMatchLocation(routes.buildSchemaPage(), { locationToMatch: pathname }),
            onClick: () => history.push(routes.buildSchemaPage(currentProjectName))
        },
        {
            iconName: 'Tag',
            text: localize(messages.tag),
            isSelected: doesRouteMatchLocation(routes.dataTaggingPage(), { locationToMatch: pathname }),
            onClick: () => history.push(routes.dataTaggingPage(currentProjectName))
        },
        {
            iconName: 'MachineLearning',
            text: localize(messages.train),
            isSelected: doesRouteMatchLocation(routes.trainingPage(), { locationToMatch: pathname }),
            onClick: () => history.push(routes.trainingPage(currentProjectName))
        },
        {
            id: moreDetailsItemIdRef.current,
            iconName: 'Product',
            text: localize(messages.modelDetails),
            secondaryText: FeatureToggles.CluModelGuidance && localize(messages.preview),
            isSelected: doesRouteMatchLocation(routes.modelDetailsListPage(), { locationToMatch: pathname }),
            onClick: () => history.push(routes.modelDetailsListPage(currentProjectName))
        },
        {
            iconName: 'Deploy',
            text: localize(messages.deploy),
            isSelected: doesRouteMatchLocation(routes.deployModelPage(), { locationToMatch: pathname }),
            onClick: () => history.push(routes.deployModelPage(currentProjectName))
        },
        {
            iconName: 'TestBeaker',
            text: localize(messages.testModel),
            isSelected: doesRouteMatchLocation(routes.testModelsPage(), { locationToMatch: pathname }),
            onClick: () => history.push(routes.testModelsPage(currentProjectName))
        },
        {
            iconName: 'Settings',
            text: localize(messages.projectSettings),
            isSelected: doesRouteMatchLocation(routes.projectSettingsPage(), { locationToMatch: pathname }),
            onClick: () => history.push(routes.projectSettingsPage(currentProjectName))
        }
    ];

    return currentCluProjectStore ? <LuisSidebar itemLists={[items]} /> : null;
});
